<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { Form, Field, ErrorMessage } from 'vee-validate'
import { useI18n } from '@/composable/useI18n'
import { useFareOptimizerStore } from '@/store/fareOptimizer'
import { useSuccessStore } from '@/store/success'
import { energyConsumerUnitSchema } from '@/schemas/EnergyConsumerUnitSchema'
import { IEnergyConsumerUnit } from '@/interfaces/entities/energyConsumerUnit'
import IcLoader from '@/components/Icons/IcLoader.vue'

const props = defineProps<{
  isOpen: boolean
  unit?: IEnergyConsumerUnit | null
}>()
const emit = defineEmits<{ (e: 'close'): void }>()

const { t } = useI18n()
const store = useFareOptimizerStore()
const isSubmitting = ref(false)

const isEditMode = computed(() => props.unit != null)

const initialValues = ref({
  code: '',
  name: '',
})

watch(
  () => [props.isOpen, props.unit] as const,
  ([open, unit]) => {
    if (!open) return
    initialValues.value = {
      code: unit?.code ?? '',
      name: unit?.name ?? '',
    }
  },
  { immediate: true },
)

async function onSubmit(values: Record<string, unknown>) {
  isSubmitting.value = true
  try {
    const code = String(values.code ?? '').trim()
    const name = String(values.name ?? '').trim()

    if (isEditMode.value && props.unit) {
      const updated = await store.updateConsumerUnit(props.unit.id, { code, name })
      if (!updated) return
      useSuccessStore().setSuccess([t('contract_optimizer.consumer_units.update_success')])
    } else {
      const created = await store.createConsumerUnit({ code, name, active: true })
      if (!created) return
      useSuccessStore().setSuccess([t('contract_optimizer.consumer_units.create_success')])
    }
    emit('close')
  } finally {
    isSubmitting.value = false
  }
}
</script>

<template>
  <div v-if="isOpen" class="fixed inset-0 z-[60] flex items-center justify-center p-4">
    <div class="absolute inset-0 bg-black/40" @click="emit('close')" />
    <div class="relative w-full max-w-lg rounded-none bg-white p-6 shadow-xl">
      <h3 class="text-lg font-semibold text-slate-900">
        {{
          isEditMode
            ? t('contract_optimizer.consumer_units.edit_title')
            : t('contract_optimizer.consumer_units.create_title')
        }}
      </h3>

      <Form
        :key="`${unit?.id ?? 'new'}-${isOpen}`"
        class="mt-4 flex flex-col gap-4"
        :validation-schema="energyConsumerUnitSchema"
        :initial-values="initialValues"
        @submit="onSubmit">
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-slate-700">
            {{ t('contract_optimizer.consumer_units.fields.code') }}
          </label>
          <Field
            name="code"
            type="text"
            class="h-11 w-full rounded-none border-2 border-stroke px-4 focus:border-blue focus:outline-none" />
          <ErrorMessage name="code" class="text-sm text-red" />
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-slate-700">
            {{ t('contract_optimizer.consumer_units.fields.name') }}
          </label>
          <Field
            name="name"
            type="text"
            class="h-11 w-full rounded-none border-2 border-stroke px-4 focus:border-blue focus:outline-none" />
          <ErrorMessage name="name" class="text-sm text-red" />
        </div>

        <div class="flex justify-end gap-3 border-t border-stroke pt-4">
          <button
            type="button"
            class="rounded-none border border-stroke px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            @click="emit('close')">
            {{ t('contract_optimizer.invoice.cancel') }}
          </button>
          <button
            type="submit"
            :disabled="isSubmitting"
            class="inline-flex items-center gap-2 rounded-none bg-primary px-4 py-2 text-sm font-medium text-white transition hover:bg-opacity-90 disabled:cursor-not-allowed disabled:opacity-50">
            <IcLoader v-if="isSubmitting" class="h-4 w-4 animate-spin" />
            {{
              isEditMode
                ? t('contract_optimizer.consumer_units.edit_submit')
                : t('contract_optimizer.consumer_units.create_submit')
            }}
          </button>
        </div>
      </Form>
    </div>
  </div>
</template>
